import { useCallback, useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MarkerType,
  MiniMap,
  type Connection,
  type Edge,
  type Node,
  useEdgesState,
  useNodesState,
  type OnNodesDelete,
} from "reactflow"
import "reactflow/dist/style.css"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { useToast } from "@/hooks/use-toast"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { broadcastNodeTypes } from "@/components/broadcast/BroadcastNodeTypes"
import { BroadcastSidebar } from "@/components/broadcast/BroadcastSidebar"
import { BroadcastPropertiesPanel } from "@/components/broadcast/BroadcastPropertiesPanel"
import { apiRequest, type ApiError } from "@/lib/api"
import type { Workflow, WorkflowChannel } from "@/types"
import type { WorkflowNodeData } from "@/types/workflow"
import {
  Save,
  History,
  Square,
  Play,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Clock,
  BarChart3
} from "lucide-react"
import { cn } from "@/lib/utils"

type WorkflowResponse = {
  workflow: Workflow
}

const generateNodeId = (type: string) => {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 9)
  return `${type}-${timestamp}-${random}`
}

// Initialize workflow with audience node
const createInitialWorkflow = () => {
  const audienceId = generateNodeId('audience')
  const nodes: Node<WorkflowNodeData>[] = [
    {
      id: audienceId,
      type: 'audience',
      position: { x: 250, y: 100 },
      data: {
        targetType: 'all',
        contactCount: 0,
      },
    },
  ]
  const edges: Edge[] = []
  return { nodes, edges, audienceId }
}

const WorkflowBuilderPage = () => {
  const { projectSlug, workflowId } = useParams<{ projectSlug: string; workflowId: string }>()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [nodes, setNodes, onNodesChange] = useNodesState<WorkflowNodeData>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [lastBackupTime, setLastBackupTime] = useState<Date | null>(null)
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [backupToRestore, setBackupToRestore] = useState<any>(null)

  const workflowBase = `/api/projects/${encodeURIComponent(projectSlug || "")}/workflows/${workflowId}`

  const workflowQuery = useQuery<WorkflowResponse, ApiError>({
    queryKey: ["workflow", projectSlug, workflowId],
    queryFn: () => apiRequest<WorkflowResponse>(workflowBase),
    enabled: Boolean(projectSlug && workflowId),
  })

  // Initialize or load workflow
  useEffect(() => {
    if (!workflowQuery.data || !workflowId) return
    const workflow = workflowQuery.data.workflow

    // Check for localStorage backup first
    const backupKey = `workflow_backup_${workflowId}`
    const backupData = localStorage.getItem(backupKey)

    if (backupData) {
      try {
        const backup = JSON.parse(backupData)
        const backupDate = new Date(backup.timestamp)
        const now = new Date()
        const minutesAgo = (now.getTime() - backupDate.getTime()) / 1000 / 60

        // If backup is recent (within 30 minutes) and workflow is empty, offer to restore
        if (minutesAgo < 30 && (!workflow.definition || !workflow.definition.nodes)) {
          const restore = confirm(
            `Encontramos um backup recente deste workflow (${Math.round(minutesAgo)} minutos atrás). Deseja restaurar?`
          )
          if (restore) {
            setNodes(backup.nodes)
            setEdges(backup.edges)
            console.log('Restored workflow from backup')
            return
          }
        }
      } catch (error) {
        console.error('Failed to parse backup:', error)
      }
    }

    // If workflow has existing definition with nodes/edges, load them
    // Otherwise initialize with audience node
    if (workflow.definition && typeof workflow.definition === 'object') {
      const def = workflow.definition as any

      // React Flow format: nodes as array
      let nodesToLoad: Node<WorkflowNodeData>[] = []
      let edgesToLoad: Edge[] = []

      if (Array.isArray(def.nodes) && Array.isArray(def.edges)) {
        nodesToLoad = def.nodes
        edgesToLoad = def.edges
      } else if (def.nodes && typeof def.nodes === 'object' && !Array.isArray(def.nodes) && Array.isArray(def.edges)) {
        // Backend format: nodes as hash { id => node }, edges with from/to
        // Map backend types back to React Flow component types
        const backendToFrontendType: Record<string, string> = {
          send_message: 'message',
          send_facebook_message: 'facebook_message',
          wait_for_seconds: 'delay',
          end_workflow: 'end',
          audience: 'audience',
        }
        nodesToLoad = Object.entries(def.nodes).map(([id, node]: [string, any]) => ({
          id,
          type: backendToFrontendType[node.type] || node.type || 'message',
          position: node.position || { x: 0, y: 0 },
          data: node.data || node,
        }))
        edgesToLoad = def.edges.map((e: any) => ({
          id: e.id || `e-${e.from}-${e.to}`,
          source: e.source ?? e.from,
          target: e.target ?? e.to,
          type: e.type,
          animated: e.animated,
        }))
      }

      if (nodesToLoad.length > 0) {
        setNodes(nodesToLoad)
        setEdges(edgesToLoad)
        return
      }
    }

    // Initialize new workflow
    const { nodes: initialNodes, edges: initialEdges } = createInitialWorkflow()
    setNodes(initialNodes)
    setEdges(initialEdges)
  }, [workflowQuery.data, workflowId, setNodes, setEdges])

  const saveMutation = useMutation<{ workflow: Workflow }, ApiError, any>({
    mutationFn: (payload) =>
      apiRequest<{ workflow: Workflow }>(workflowBase, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow", projectSlug, workflowId] })
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug] })
      toast({
        title: "Success!",
        description: "Workflow saved successfully",
      })
    },
    onError: (error) => {
      console.error('Save error:', error)
      toast({
        title: "Save failed",
        description: error.message || 'Unknown error. Check console for details.',
        variant: "destructive",
      })
    },
  })

  const stopMutation = useMutation<{ workflow: Workflow }, ApiError, void>({
    mutationFn: () =>
      apiRequest<{ workflow: Workflow }>(`${workflowBase}/stop`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow", projectSlug, workflowId] })
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug] })
      toast({
        title: "Workflow stopped",
        description: "You can now edit the workflow",
      })
    },
    onError: (error) => {
      console.error('Stop error:', error)
      toast({
        title: "Failed to stop workflow",
        description: error.message || 'Unknown error',
        variant: "destructive",
      })
    },
  })

  const handleConnect = useCallback((connection: Connection) => {
    setEdges((eds) =>
      addEdge(
        {
          ...connection,
          type: 'smoothstep',
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed },
        },
        eds
      )
    )
  }, [setEdges])

  const handleDragStart = useCallback((event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType)
    event.dataTransfer.effectAllowed = 'move'
  }, [])

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()

      const reactFlowBounds = event.currentTarget.getBoundingClientRect()
      const type = event.dataTransfer.getData('application/reactflow')

      if (!type) return

      const position = {
        x: event.clientX - reactFlowBounds.left - 120,
        y: event.clientY - reactFlowBounds.top - 40,
      }

      const newNodeId = generateNodeId(type)
      const newNode: Node<WorkflowNodeData> = {
        id: newNodeId,
        type,
        position,
        data:
          type === 'message'
            ? {
                messageMode: 'custom',
                pushTitle: '',
                pushDescription: '',
                pushUrl: '',
                pushImageUrl: '',
                contents: [],
              }
            : type === 'facebook_message'
            ? {
                templateLanguage: 'pt_BR',
                templateParams: '',
                messageTag: 'RESPONSE',
                bodyHasVariables: true,
                urlButtonsCount: 0,
                campaignName: '',
                contents: [],
              }
            : type === 'delay'
            ? { delay: '5', unit: 'minutos' }
            : {},
      }

      setNodes((nds) => nds.concat(newNode))
    },
    [setNodes]
  )

  const handleUpdateNodeData = useCallback(
    (nodeId: string, data: Partial<WorkflowNodeData>) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
        )
      )
    },
    [setNodes]
  )

  const handleDuplicateNode = useCallback(
    (nodeId: string) => {
      const nodeToDuplicate = nodes.find((n) => n.id === nodeId)
      if (!nodeToDuplicate || nodeToDuplicate.type === 'audience') return

      const newNodeId = generateNodeId(nodeToDuplicate.type || 'message')
      const newNode: Node<WorkflowNodeData> = {
        ...nodeToDuplicate,
        id: newNodeId,
        position: {
          x: nodeToDuplicate.position.x + 50,
          y: nodeToDuplicate.position.y + 50,
        },
        data: { ...nodeToDuplicate.data },
      }

      setNodes((nds) => nds.concat(newNode))
    },
    [nodes, setNodes]
  )

  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((node) => node.id !== nodeId))
      setEdges((eds) =>
        eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
      )
      if (selectedNodeId === nodeId) {
        setSelectedNodeId(null)
      }
    },
    [setNodes, setEdges, selectedNodeId]
  )

  const handleNodesDelete: OnNodesDelete = useCallback(
    (deleted) => {
      // Prevent deletion of audience node
      const audienceNode = deleted.find((n) => n.type === 'audience')
      if (audienceNode) {
        toast({
          title: "Cannot delete audience node",
          description: "The audience node is required for the workflow",
          variant: "destructive",
        })
        return
      }
    },
    [toast]
  )

  // Auto-save to localStorage as backup
  useEffect(() => {
    if (!workflowId || nodes.length === 0) return

    const backup = {
      nodes,
      edges,
      timestamp: new Date().toISOString(),
    }

    try {
      localStorage.setItem(`workflow_backup_${workflowId}`, JSON.stringify(backup))
      setLastBackupTime(new Date())
      console.log('Workflow backed up to localStorage')
    } catch (error) {
      console.error('Failed to backup workflow:', error)
    }
  }, [nodes, edges, workflowId])

  const isDraft = workflowQuery.data?.workflow.status === "draft"
  const workflowType = workflowQuery.data?.workflow.workflow_type ?? "broadcast"
  const workflowChannel: WorkflowChannel = workflowQuery.data?.workflow.channel ?? "push"
  const isAutomationWorkflow = workflowType === "workflow"
  const listPath = `/${projectSlug}/${isAutomationWorkflow ? "workflows" : "broadcasts"}`
  const runPath = `/${projectSlug}/broadcasts/${workflowId}/run`
  const workflowTypeLabel = isAutomationWorkflow ? "Workflow" : "Broadcast"
  const channelLabel = workflowChannel === "facebook_message" ? "Messenger" : "Push"

  const handleSave = useCallback(() => {
    if (!isDraft) {
      toast({
        title: "Cannot edit active workflow",
        description: "Click 'Stop to Edit' first",
        variant: "destructive",
      })
      return
    }
    try {
      // Clean nodes and edges for serialization (remove React Flow internals)
      const cleanNodes = nodes.map(node => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: node.data,
      }))

      const cleanEdges = edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: edge.type,
        animated: edge.animated,
      }))

      const definition = {
        nodes: cleanNodes,
        edges: cleanEdges,
      }

      console.log('Saving workflow definition:', definition)
      saveMutation.mutate({ workflow: { definition } })
    } catch (error) {
      console.error('Failed to save workflow:', error)
      toast({
        title: "Save failed",
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: "destructive",
      })
    }
  }, [nodes, edges, saveMutation, isDraft, toast])

  const handleRestoreBackup = useCallback(() => {
    if (!workflowId) return

    const backupKey = `workflow_backup_${workflowId}`
    const backupData = localStorage.getItem(backupKey)

    if (!backupData) {
      toast({
        title: "No backup found",
        description: "There is no local backup available",
        variant: "destructive",
      })
      return
    }

    try {
      const backup = JSON.parse(backupData)
      setBackupToRestore(backup)
      setRestoreDialogOpen(true)
    } catch (error) {
      console.error('Failed to restore backup:', error)
      toast({
        title: "Failed to restore backup",
        description: "Backup data is corrupted",
        variant: "destructive",
      })
    }
  }, [workflowId, toast])

  const confirmRestore = useCallback(() => {
    if (!backupToRestore) return
    
    setNodes(backupToRestore.nodes)
    setEdges(backupToRestore.edges)
    setRestoreDialogOpen(false)
    setBackupToRestore(null)
    toast({
      title: "Backup restored",
      description: "Workflow has been restored from local backup",
    })
  }, [backupToRestore, setNodes, setEdges, toast])

  // Keyboard shortcut for save (Cmd/Ctrl + S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        if (isDraft) {
          handleSave()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDraft, handleSave])

  const selectedNode = nodes.find((n) => n.id === selectedNodeId)
  const selectedNodeData = selectedNode?.data || null

  const getTimeSinceBackup = () => {
    if (!lastBackupTime) return null
    const seconds = Math.floor((Date.now() - lastBackupTime.getTime()) / 1000)
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.floor(seconds / 60)
    return `${minutes}m ago`
  }

  return (
    <>
      <div className="flex flex-col h-full overflow-hidden">
        {/* Compact toolbar */}
        <div className="flex items-center h-12 px-3 border-b bg-background gap-1 shrink-0">
          <SidebarTrigger className="h-8 w-8" />
          <Separator orientation="vertical" className="h-4 mx-1" />
          <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
            <Link to={listPath}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <Separator orientation="vertical" className="h-4 mx-1" />

          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold truncate max-w-48">
              {workflowQuery.data?.workflow.name || "Workflow Builder"}
            </span>
            {workflowQuery.data && (
              <>
                <Badge
                  variant="secondary"
                  className={cn(
                    "gap-1 text-xs",
                    isDraft ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-700 border-green-200"
                  )}
                >
                  {isDraft ? <Clock className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                  {workflowQuery.data.workflow.status}
                </Badge>
                <Badge variant="outline" className="text-xs hidden sm:inline-flex">{workflowTypeLabel}</Badge>
                <Badge variant="outline" className="text-xs hidden sm:inline-flex">{channelLabel}</Badge>
              </>
            )}
            {lastBackupTime && (
              <span className="text-[10px] text-muted-foreground hidden md:block">
                · saved {getTimeSinceBackup()}
              </span>
            )}
          </div>

          <div className="flex-1" />

          {/* Action buttons */}
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRestoreBackup}
              className="h-8 gap-1.5 text-xs"
            >
              <History className="h-3.5 w-3.5" />
              <span className="hidden sm:block">Restore</span>
            </Button>

            {isDraft ? (
              <Button
                onClick={handleSave}
                disabled={saveMutation.isPending}
                size="sm"
                className="h-8 gap-1.5 text-xs"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                {saveMutation.isPending ? "Saving..." : "Save"}
                {!saveMutation.isPending && (
                  <kbd className="pointer-events-none inline-flex h-4 select-none items-center gap-0.5 rounded border bg-white/20 px-1 font-mono text-[9px] font-medium opacity-80">
                    <span>⌘</span>S
                  </kbd>
                )}
              </Button>
            ) : (
              <Button
                onClick={() => stopMutation.mutate()}
                disabled={stopMutation.isPending}
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
              >
                {stopMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Square className="h-3.5 w-3.5" />
                )}
                {stopMutation.isPending ? "Stopping..." : "Stop to Edit"}
              </Button>
            )}

            {!isAutomationWorkflow ? (
              <Button variant="default" size="sm" asChild disabled={!workflowQuery.data} className="h-8 gap-1.5 text-xs">
                <Link to={runPath}>
                  <Play className="h-3.5 w-3.5" />
                  Run
                </Link>
              </Button>
            ) : (
              <Button variant="default" size="sm" asChild disabled={!workflowQuery.data} className="h-8 gap-1.5 text-xs">
                <Link to={`/${projectSlug}/workflows/${workflowId}/analytics`}>
                  <BarChart3 className="h-3.5 w-3.5" />
                  Analytics
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Active workflow warning */}
        {!isDraft && !workflowQuery.isLoading && !workflowQuery.isError && (
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 shrink-0">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            This workflow is active and cannot be edited. Click "Stop to Edit" to make changes.
          </div>
        )}

        {/* Loading state */}
        {workflowQuery.isLoading && (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error state */}
        {workflowQuery.isError && (
          <div className="p-6">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-800">
                {workflowQuery.error?.message ?? "Failed to load workflow"}
              </p>
            </div>
          </div>
        )}

        {/* Editor */}
        {!workflowQuery.isLoading && !workflowQuery.isError && (
          <div className="flex flex-1 overflow-hidden">
            <BroadcastSidebar
              channel={workflowChannel}
              onDragStart={handleDragStart}
            />

            <div className="flex-1 relative">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={broadcastNodeTypes}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={handleConnect}
                onNodesDelete={handleNodesDelete}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onNodeClick={(_, node) => setSelectedNodeId(node.id)}
                onPaneClick={() => setSelectedNodeId(null)}
                fitView
                snapToGrid
                snapGrid={[15, 15]}
                defaultEdgeOptions={{
                  type: 'smoothstep',
                  animated: true,
                  markerEnd: { type: MarkerType.ArrowClosed },
                }}
              >
                <MiniMap />
                <Controls />
                <Background />
              </ReactFlow>
            </div>

            <BroadcastPropertiesPanel
              selectedNode={selectedNodeId}
              selectedNodeType={selectedNode?.type ?? null}
              channel={workflowChannel}
              nodeData={selectedNodeData}
              onUpdateNodeData={handleUpdateNodeData}
              onDuplicateNode={handleDuplicateNode}
              onDeleteNode={handleDeleteNode}
            />
          </div>
        )}
      </div>

      {/* Restore Backup Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore from Backup?</AlertDialogTitle>
            <AlertDialogDescription>
              {backupToRestore && (
                <>
                  This will restore the workflow from a local backup created on{' '}
                  <strong>
                    {new Date(backupToRestore.timestamp).toLocaleString()}
                  </strong>
                  .
                  <br /><br />
                  This action will replace the current workflow state. Any unsaved changes will be lost.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setRestoreDialogOpen(false)
              setBackupToRestore(null)
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmRestore}>
              Restore Backup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default WorkflowBuilderPage
